import React from "react";
import { Link } from "react-router-dom";
import { ScanSearch, Cpu, Calculator, ArrowRight } from "lucide-react";
import { buildAeoDefinition } from "../../data/avmContent";

// Blocks 2 (AEO direct-answer callout) + 3 (Multi-AI / 3-Portal methodology cards).
// Shared by city and neighborhood landing pages. Describes the real stack only.
export default function MultiAiAvmSection({ locationName, taxMeasure }) {
  const cards = [
    [ScanSearch, "3-Portal AVM Benchmarking",
      `Cross-checks a live Zillow Zestimate® (via Bridge Interactive) with Redfin and Homes.com figures against MLS comparable sales, so your ${locationName} value reflects several independent models — not a single black-box estimate.`],
    [Cpu, "Multi-AI Comparative Market Engine",
      `Three independent AI appraisers — Google Gemini, OpenAI, and Anthropic Claude — each weigh ${locationName} comparables for proximity, recency, size, and lot nuances, then reconcile to a defensible range with the adjustment math shown.`],
    [Calculator, "Real Net Equity Calculation",
      `Instantly deducts ${taxMeasure} and models a 1% Equitas listing fee against a traditional 3%, so you see the equity you actually keep — not just a headline price.`],
  ];

  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-14" data-testid="multi-ai-avm-section">
      <span className="eyebrow">Multi-AI · 3-Portal AVM</span>
      <h2 className="mt-2 font-serif text-3xl text-brand-ink">
        How the {locationName} valuation tool works
      </h2>

      {/* Block 2 — AEO direct-answer callout */}
      <div className="mt-6 rounded-2xl border-l-4 border-brand-gold bg-white p-6 shadow-[0_4px_20px_rgba(11,30,140,0.04)]" data-testid="aeo-answer-block">
        <p className="text-sm font-semibold text-brand-emerald">
          What is the {locationName} Multi-AI Home Valuation Tool?
        </p>
        <p className="mt-2 text-sm sm:text-base text-brand-stone leading-relaxed">
          {buildAeoDefinition(locationName, taxMeasure)}
        </p>
      </div>

      {/* Block 3 — methodology cards */}
      <div className="mt-6 grid md:grid-cols-3 gap-5">
        {cards.map(([Icon, title, body]) => (
          <div key={title} className="rounded-2xl bg-white border border-brand-hairline p-6 shadow-[0_4px_20px_rgba(11,30,140,0.04)]" data-testid="avm-method-card">
            <Icon className="h-6 w-6 text-brand-gold" />
            <h3 className="mt-4 font-serif text-xl text-brand-ink">{title}</h3>
            <p className="mt-2 text-sm text-brand-stone leading-relaxed">{body}</p>
          </div>
        ))}
      </div>

      <Link to="/how-it-works" data-testid="avm-methodology-link"
        className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-emerald hover:underline">
        See the full 3-Portal &amp; Multi-AI methodology <ArrowRight className="h-4 w-4" />
      </Link>
    </section>
  );
}
